import { create } from 'zustand'
import axios from 'axios'
import type { CVData, CVRecord, AnalysisResult } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface CVState {
  cvs: CVRecord[]
  currentCV: CVRecord | null
  cvData: CVData | null
  analysis: AnalysisResult | null
  loading: boolean
  error: string | null

  fetchCVs: () => Promise<void>
  fetchCV: (id: string) => Promise<void>
  saveCV: (cv: CVData, jobDescription?: string, name?: string) => Promise<string>
  updateCV: (id: string, cv: CVData, jobDescription?: string) => Promise<void>
  uploadCV: (cv: CVData, jobDescription?: string, name?: string) => Promise<string>
  deleteCV: (id: string) => Promise<void>
  renameCV: (id: string, name: string) => Promise<void>
  analyzeCV: (cv: CVData, jobDescription: string) => Promise<void>
  optimizeSection: (cv: CVData, section: string, content: any, jobDescription?: string) => Promise<any>
  paraphrase: (content: string, section: string, context?: string) => Promise<any>
  paraphraseSection: (cv: CVData, section: string, jobDescription?: string) => Promise<any>
  categorizeSkills: (skills: string[]) => Promise<any>
  parseCV: (cvText: string) => Promise<any>
  generatePDF: (cv: CVData) => Promise<string>
  setCVData: (data: CVData) => void
  clearCurrent: () => void
  addWorkExperience: () => void
  removeWorkExperience: (index: number) => void
  addProject: () => void
  removeProject: (index: number) => void
  addEducation: () => void
  removeEducation: (index: number) => void
  addCourse: () => void
  removeCourse: (index: number) => void
}

function getToken() {
  return localStorage.getItem('auth_token')
}

function authHeaders() {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export const useCVStore = create<CVState>((set, get) => ({
  cvs: [],
  currentCV: null,
  cvData: null,
  analysis: null,
  loading: false,
  error: null,

  fetchCVs: async () => {
    set({ loading: true, error: null })
    try {
      const res = await axios.get(`${API_BASE_URL}/api/cv`, { headers: authHeaders() })
      set({ cvs: res.data.cvs, loading: false })
    } catch {
      set({ error: 'Failed to fetch CVs', loading: false })
    }
  },

  fetchCV: async (id: string) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.get(`${API_BASE_URL}/api/cv/${id}`, { headers: authHeaders() })
      const cv = res.data
      set({
        currentCV: cv,
        cvData: JSON.parse(cv.optimized_cv || cv.original_cv),
        loading: false,
      })
    } catch {
      set({ error: 'Failed to fetch CV', loading: false })
    }
  },

  saveCV: async (cv: CVData, jobDescription?: string, name?: string) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/save`, { cv, jobDescription, name }, { headers: authHeaders() })
      await get().fetchCVs()
      set({ loading: false })
      return res.data.id
    } catch {
      set({ error: 'Failed to save CV', loading: false })
      throw new Error('Failed to save CV')
    }
  },

  updateCV: async (id: string, cv: CVData, jobDescription?: string) => {
    set({ loading: true, error: null })
    try {
      await axios.put(`${API_BASE_URL}/api/cv/${id}`, { cv, jobDescription }, { headers: authHeaders() })
      await get().fetchCVs()
      set({ loading: false })
    } catch {
      set({ error: 'Failed to update CV', loading: false })
    }
  },

  uploadCV: async (cv: CVData, jobDescription?: string, name?: string) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/upload`, { cv, jobDescription, name }, { headers: authHeaders() })
      await get().fetchCVs()
      set({ loading: false })
      return res.data.id
    } catch {
      set({ error: 'Failed to upload CV', loading: false })
      throw new Error('Failed to upload CV')
    }
  },

  deleteCV: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await axios.delete(`${API_BASE_URL}/api/cv/${id}`, { headers: authHeaders() })
      await get().fetchCVs()
      if (get().currentCV?.id === id) {
        set({ currentCV: null, cvData: null })
      }
      set({ loading: false })
    } catch {
      set({ error: 'Failed to delete CV', loading: false })
    }
  },

  renameCV: async (id: string, name: string) => {
    set({ loading: true, error: null })
    try {
      await axios.patch(`${API_BASE_URL}/api/cv/${id}/rename`, { name }, { headers: authHeaders() })
      await get().fetchCVs()
      set({ loading: false })
    } catch {
      set({ error: 'Failed to rename CV', loading: false })
    }
  },

  analyzeCV: async (cv: CVData, jobDescription: string) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/analyze`, { cv, jobDescription }, { headers: authHeaders() })
      set({ analysis: res.data, loading: false })
    } catch {
      set({ error: 'Failed to analyze CV', loading: false })
    }
  },

  optimizeSection: async (cv: CVData, section: string, content: any, jobDescription?: string) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/optimize-section`, { cv, section, content, jobDescription }, { headers: authHeaders() })
      set({ loading: false })
      return res.data
    } catch {
      set({ error: 'Failed to optimize section', loading: false })
      throw new Error('Failed to optimize section')
    }
  },

  paraphrase: async (content: string, section: string, context?: string) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/paraphrase`, { content, section, context }, { headers: authHeaders() })
      set({ loading: false })
      return res.data
    } catch {
      set({ error: 'Failed to paraphrase', loading: false })
      throw new Error('Failed to paraphrase')
    }
  },

  paraphraseSection: async (cv: CVData, section: string, jobDescription?: string) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/paraphrase-section`, { cv, section, jobDescription }, { headers: authHeaders() })
      set({ loading: false })
      return res.data
    } catch {
      set({ error: 'Failed to paraphrase section', loading: false })
      throw new Error('Failed to paraphrase section')
    }
  },

  categorizeSkills: async (skills: string[]) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/categorize-skills`, { skills }, { headers: authHeaders() })
      set({ loading: false })
      return res.data
    } catch {
      set({ error: 'Failed to categorize skills', loading: false })
      throw new Error('Failed to categorize skills')
    }
  },

  parseCV: async (cvText: string) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/parse`, { cvText }, { headers: authHeaders() })
      set({ cvData: res.data.cv, loading: false })
      return res.data.cv
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to parse CV'
      set({ error: msg, loading: false })
      throw err
    }
  },

  generatePDF: async (cv: CVData) => {
    set({ loading: true, error: null })
    try {
      const res = await axios.post(`${API_BASE_URL}/api/cv/generate-pdf`, { cv }, { headers: authHeaders() })
      set({ loading: false })
      return res.data.html
    } catch {
      set({ error: 'Failed to generate PDF', loading: false })
      throw new Error('Failed to generate PDF')
    }
  },

  setCVData: (data: CVData) => set({ cvData: data }),

  clearCurrent: () => set({ currentCV: null, cvData: null, analysis: null }),

  addWorkExperience: () => {
    const data = get().cvData
    if (!data) return
    set({ cvData: { ...data, workExperience: [...data.workExperience, { company: '', role: '', duration: '' }] } })
  },

  removeWorkExperience: (index: number) => {
    const data = get().cvData
    if (!data) return
    const we = [...data.workExperience]
    we.splice(index, 1)
    set({ cvData: { ...data, workExperience: we } })
  },

  addProject: () => {
    const data = get().cvData
    if (!data) return
    set({ cvData: { ...data, projects: [...data.projects, { name: '', duration: '' }] } })
  },

  removeProject: (index: number) => {
    const data = get().cvData
    if (!data) return
    const p = [...data.projects]
    p.splice(index, 1)
    set({ cvData: { ...data, projects: p } })
  },

  addEducation: () => {
    const data = get().cvData
    if (!data) return
    set({ cvData: { ...data, education: [...data.education, { institution: '', major: '', year: '' }] } })
  },

  removeEducation: (index: number) => {
    const data = get().cvData
    if (!data) return
    const e = [...data.education]
    e.splice(index, 1)
    set({ cvData: { ...data, education: e } })
  },

  addCourse: () => {
    const data = get().cvData
    if (!data) return
    set({ cvData: { ...data, courses: [...data.courses, { name: '', year: '' }] } })
  },

  removeCourse: (index: number) => {
    const data = get().cvData
    if (!data) return
    const c = [...data.courses]
    c.splice(index, 1)
    set({ cvData: { ...data, courses: c } })
  },
}))
